import React from 'react';
import PropTypes from 'prop-types';
import { loadStripe } from '../../../modules/stripeV3';
import { APP, STRIPE_JS_KEY } from '../../../config';
import { getIOSVersion } from '../../../modules/AppInfo';
import { Stripe, StripeElements } from './types';

export type StripeContextType = {
  stripe: Stripe,
  elements: StripeElements,
};

/**
 * Stripe Context
 * - used by stripe to correctly render its inputs (for example the card element input)
 */
export const StripeContext = React.createContext<StripeContextType>({
  stripe: null,
  elements: null,
});

const defaultStripeOptions = {
  fonts: [{
    family: 'bentonsans',
    src: 'url("https://s3.amazonaws.com/styleseat-static/new/fonts/bentonsans-light-webfont.d5c22ab3d92effeaa0d83f15d3b28bd14128994f.woff") format("woff")',
  }],
  locale: 'en-US',
};

/**
 * Loads and initializes stripe -
 * Provides children with access to "stripe" and "elements"
 * @param apiKey Stripe API key
 * @param options
 * @param children
 * @returns {*}
 * @constructor
 */
export const StripeProvider = ({
  apiKey,
  options,
  children,
}) => {
  const [context, setContext] = React.useState(() => ({
    stripe: null,
    elements: null,
  }));

  // initializes stripe with the provided API key
  // and options (passed onto elements)
  React.useEffect(() => {
    async function load() {
      if (!apiKey || context.stripe) return;
      const isIOSAppBuild = APP && getIOSVersion();
      const stripe = await loadStripe(apiKey, isIOSAppBuild, {
        betas: ['cvc_update_beta_1'],
      }) as stripe.Stripe;
      if (stripe && stripe.elements) {
        setContext({
          stripe,
          elements: stripe.elements(options),
        });
      }
    }

    load();
  }, [apiKey]);

  return (
    <StripeContext.Provider value={context}>
      {children}
    </StripeContext.Provider>
  );
};

StripeProvider.propTypes = {
  options: PropTypes.object,
  apiKey: PropTypes.string,
  children: PropTypes.any,
};

StripeProvider.defaultProps = {
  options: { ...defaultStripeOptions },
  apiKey: null,
  children: null,
};

export const withStripeContext = WrappedComponent => {
  const WrapperComponent = props => (
    <StripeProvider apiKey={STRIPE_JS_KEY}>
      <WrappedComponent {...props} />
    </StripeProvider>
  );

  WrapperComponent.propTypes = WrappedComponent.propTypes;
  WrapperComponent.defaultProps = WrappedComponent.defaultProps;

  return WrapperComponent;
};

export default StripeContext;
